import { useMemo, useCallback, useEffect, useState } from 'react';
import { Search, X, UserCircle, Building2, Briefcase, Wrench, Tag } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ContactFilterState } from '@/hooks/useContactFilters';
import { Contact } from '@/hooks/useContacts';

interface ContactsFilterBarProps {
  filters: ContactFilterState;
  onFilterChange: (filters: Partial<ContactFilterState>) => void;
  onClearFilters: () => void;
  activeFilterCount: number;
  allContacts: Contact[];
}

const contactTypeOptions = [
  { value: 'lead', label: 'Lead', icon: UserCircle },
  { value: 'client', label: 'Client', icon: Building2 },
  { value: 'vendor', label: 'Vendor', icon: Briefcase },
  { value: 'contractor', label: 'Contractor', icon: Wrench },
];

const statusOptions = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

export function ContactsFilterBar({
  filters,
  onFilterChange,
  onClearFilters,
  activeFilterCount,
  allContacts,
}: ContactsFilterBarProps) {
  const [searchValue, setSearchValue] = useState(filters.search);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      onFilterChange({ search: searchValue });
    }, 300);

    return () => clearTimeout(timer);
  }, [searchValue, onFilterChange]);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  const handleClearSearch = () => {
    setSearchValue('');
    onFilterChange({ search: '' });
  };

  const toggleContactType = useCallback((type: string) => {
    const newTypes = filters.contactTypes.includes(type)
      ? filters.contactTypes.filter(t => t !== type)
      : [...filters.contactTypes, type];
    onFilterChange({ contactTypes: newTypes });
  }, [filters.contactTypes, onFilterChange]);

  const toggleStatus = useCallback((status: string) => {
    const newStatuses = filters.statuses.includes(status)
      ? filters.statuses.filter(s => s !== status)
      : [...filters.statuses, status];
    onFilterChange({ statuses: newStatuses });
  }, [filters.statuses, onFilterChange]);

  const toggleTag = useCallback((tag: string) => {
    const newTags = filters.tags.includes(tag)
      ? filters.tags.filter(t => t !== tag)
      : [...filters.tags, tag];
    onFilterChange({ tags: newTags });
  }, [filters.tags, onFilterChange]);

  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    allContacts.forEach(contact => {
      contact.tags?.forEach(tag => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [allContacts]);

  const removeFilter = (type: 'search' | 'contactType' | 'status' | 'tag', value?: string) => {
    switch (type) {
      case 'search':
        handleClearSearch();
        break;
      case 'contactType':
        if (value) toggleContactType(value);
        break;
      case 'status':
        if (value) toggleStatus(value);
        break;
      case 'tag':
        if (value) toggleTag(value);
        break;
    }
  };

  return (
    <Card className="p-4 shadow-sm">
      <div className="space-y-3">
        {/* Filter Controls Row */}
        <div className="flex flex-wrap gap-3 items-center">
          {/* Search Input */}
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, phone, or company..."
              value={searchValue}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-9 pr-9 bg-background"
            />
            {searchValue && (
              <button
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Contact Type Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-[150px] justify-between bg-background">
                Type
                {filters.contactTypes.length > 0 && (
                  <Badge variant="secondary" className="ml-2 rounded-full px-2">
                    {filters.contactTypes.length}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[200px] max-h-[300px] overflow-y-auto bg-popover z-50">
              {contactTypeOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <DropdownMenuCheckboxItem
                    key={option.value}
                    checked={filters.contactTypes.includes(option.value)}
                    onCheckedChange={() => toggleContactType(option.value)}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {option.label}
                  </DropdownMenuCheckboxItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Status Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-[140px] justify-between bg-background">
                Status
                {filters.statuses.length > 0 && (
                  <Badge variant="secondary" className="ml-2 rounded-full px-2">
                    {filters.statuses.length}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[180px] max-h-[300px] overflow-y-auto bg-popover z-50">
              {statusOptions.map((option) => (
                <DropdownMenuCheckboxItem
                  key={option.value}
                  checked={filters.statuses.includes(option.value)}
                  onCheckedChange={() => toggleStatus(option.value)}
                >
                  {option.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Tags Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-[160px] justify-between bg-background">
                <div className="flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  Tags
                </div>
                {filters.tags.length > 0 && (
                  <Badge variant="secondary" className="ml-2 rounded-full px-2">
                    {filters.tags.length}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[200px] max-h-[300px] overflow-y-auto bg-popover z-50">
              {allTags.length === 0 ? (
                <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                  No tags available
                </div>
              ) : (
                allTags.map((tag) => (
                  <DropdownMenuCheckboxItem
                    key={tag}
                    checked={filters.tags.includes(tag)}
                    onCheckedChange={() => toggleTag(tag)}
                  >
                    {tag}
                  </DropdownMenuCheckboxItem>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Clear All Button */}
          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              onClick={onClearFilters}
              className="gap-2"
            >
              <X className="h-4 w-4" />
              Clear all
            </Button>
          )}
        </div>

        {/* Active Filter Badges */}
        {activeFilterCount > 0 && (
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-sm text-muted-foreground">
              {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} active:
            </span>
            
            {filters.search && (
              <Badge variant="secondary" className="gap-1">
                Search: "{filters.search}"
                <button
                  onClick={() => removeFilter('search')}
                  className="hover:text-destructive ml-1"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}

            {filters.contactTypes.map(type => {
              const option = contactTypeOptions.find(o => o.value === type);
              return (
                <Badge key={type} variant="secondary" className="gap-1">
                  {option?.label}
                  <button
                    onClick={() => removeFilter('contactType', type)}
                    className="hover:text-destructive ml-1"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              );
            })}

            {filters.statuses.map(status => {
              const option = statusOptions.find(o => o.value === status);
              return (
                <Badge key={status} variant="secondary" className="gap-1">
                  {option?.label}
                  <button
                    onClick={() => removeFilter('status', status)}
                    className="hover:text-destructive ml-1"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              );
            })}

            {filters.tags.map(tag => (
              <Badge key={tag} variant="secondary" className="gap-1">
                {tag}
                <button
                  onClick={() => removeFilter('tag', tag)}
                  className="hover:text-destructive ml-1"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
